import React, { FC } from 'react';
import { isUndefined } from 'src/utils/isDefined';
import MuiAutocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import ListboxComponent from './virtualized';
import { Chip } from '@mui/material';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import { OnChangeType, OnInputChangeType, OnItemsRenderedType, OptionType } from '.';
import Checkbox from '../Checkbox';
import { SxProps, Theme } from '@mui/material/styles';

const DEFATULT_LIMIT_TAGS = 5;
const VIRTUALIZED_FROM_LENGTH = 100;

const getExpandListMaxHeight = (anchorEl: HTMLElement | null): number => {
    const viewportCap = Math.floor(window.innerHeight * 0.6);
    if (!anchorEl) {
        return viewportCap;
    }
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 16;
    const spaceAbove = rect.top - 16;
    return Math.min(viewportCap, Math.max(Math.max(spaceBelow, spaceAbove), 200));
};

const PopperMy = (props: React.ComponentProps<typeof Popper>) => {
    return <Popper {...props} style={{ width: 'fit-content' }} placement="bottom-start" />;
};

const PopperExpandList = (props: React.ComponentProps<typeof Popper>) => {
    const anchorEl = props.anchorEl as HTMLElement | null;
    const maxHeight = getExpandListMaxHeight(anchorEl);

    return (
        <Popper
            {...props}
            placement="bottom-start"
            modifiers={[
                { name: 'offset', options: { offset: [0, 4] } },
                { name: 'preventOverflow', enabled: true, options: { padding: 8, altAxis: true } },
                { name: 'flip', enabled: true },
            ]}
            style={{
                ...props.style,
                zIndex: 1300,
                ['--expand-list-max-height' as string]: `${maxHeight}px`,
            }}
        />
    );
};
export interface AutocompleteProps {
    value: any | any[];
    onChange?: OnChangeType;
    variant?: 'filled' | 'outlined' | 'standard';
    size?: 'small' | 'medium';
    error?: boolean;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    multiple?: boolean;
    portal?: boolean;
    virtualized?: boolean;
    loading?: boolean;
    label?: string;
    name?: string;
    helperText?: string;
    minWidth?: number;
    options: OptionType[];
    limitTags?: number; //pocet max zobrazenych pri multiple
    onItemsRendered?: OnItemsRenderedType;
    onInputChange?: OnInputChangeType;
    onFocus?: () => void;
    onBlur?: () => void;
    optionsExpandWidth?: boolean;
    disableVirtualized?: boolean;
    optionStyleFce?: (option: OptionType) => SxProps<Theme> | null;
    expandList?: boolean;
    placeholder?: string;
    /** Pokud je zadáno (i null), přebírá styl inputu místo hodnoty vypočtené z optionStyleFce. */
    inputSx?: SxProps<Theme> | null;
}

const Autocomplete: FC<AutocompleteProps> = (props) => {
    const {
        value,
        onChange,
        variant,
        size,
        error,
        required,
        fullWidth,
        multiple,
        label,
        name,
        helperText,
        options,
        limitTags,
        portal,
        virtualized,
        minWidth,
        onBlur,
        onFocus,
        onInputChange,
        onItemsRendered,
        disabled,
        optionsExpandWidth,
        disableVirtualized,
        optionStyleFce,
        expandList,
        placeholder,
        inputSx,
    } = props;

    const handleChange = (e: React.SyntheticEvent<Element, Event>, newValues: OptionType[] | OptionType | null) => {
        if (
            // @ts-ignore
            e._reactName === 'onKeyDown' &&
            Array.isArray(newValues) &&
            Array.isArray(value) &&
            newValues.length < value.length
        ) {
            // dont remove items by beckspace
            return;
        }

        let error = false;
        if (required && (!newValues || (!Array.isArray(newValues) && (newValues.value === undefined || newValues.value === null)))) {
            error = true;
        }
        if (onChange) {
            if (newValues === null || (Array.isArray(newValues) && !newValues.length)) {
                onChange(multiple ? [] : null, !!required);
            } else {
                if (Array.isArray(newValues)) {
                    onChange(
                        newValues.map((newValue: OptionType) => newValue.value),
                        error
                    );
                } else {
                    onChange(newValues.value, error);
                }
            }
        }
    };

    const getValue = () => {
        if (multiple) {
            const selectedValues = value || [];
            return options.filter((option) => selectedValues.includes(option.value));
        } else {
            let selectedValue = value;
            if (isUndefined(selectedValue) || selectedValue === null) {
                selectedValue = '';
            }
            return options.find((option) => option && selectedValue === option.value) || null;
        }
    };

    const selectValues = getValue();

    const limitTagsMax = limitTags || DEFATULT_LIMIT_TAGS;

    const selectedOptionForStyle =
        !multiple && selectValues && !Array.isArray(selectValues) ? (selectValues as OptionType) : null;
    const computedInputSx =
        selectedOptionForStyle && optionStyleFce ? optionStyleFce(selectedOptionForStyle) : null;
    const selectedInputSx = inputSx !== undefined ? inputSx : computedInputSx;

    const expandListMaxHeight = expandList ? Math.floor(window.innerHeight * 0.6) : undefined;
    const ListboxProps = {
        ...(onItemsRendered ? { onItemsRendered, role: 'list-box' as const } : {}),
        ...(expandList ? { expandList: true } : {}),
        ...(expandList
            ? {
                  sx: {
                      maxHeight: 'var(--expand-list-max-height, 60vh)',
                      overflowY: 'auto' as const,
                  },
              }
            : expandListMaxHeight
              ? { sx: { maxHeight: expandListMaxHeight, overflowY: 'auto' as const } }
              : {}),
    };
    const hasListboxProps = Object.keys(ListboxProps).length > 0;
    const useVirtualizedList =
        !disableVirtualized && (virtualized || options.length > VIRTUALIZED_FROM_LENGTH);

    const PopperComponent = optionsExpandWidth
        ? PopperMy
        : expandList
          ? PopperExpandList
          : undefined;

    return (
        <MuiAutocomplete
            disablePortal={!(portal || expandList)}
            disabled={disabled}
            multiple={multiple}
            value={selectValues}
            disableCloseOnSelect={!!multiple}
            limitTags={limitTagsMax}
            options={options}
            loading={props.loading}
            getOptionLabel={(option: OptionType) => option && option.label}
            onChange={handleChange}
            disableClearable={required}
            // poper display options to full width of text
            PopperComponent={PopperComponent}
            // todo: toto asi bude dobre pre rozdelenie na to aky select to bude
            ListboxComponent={useVirtualizedList ? ListboxComponent : undefined}
            // @ts-ignore
            ListboxProps={hasListboxProps ? ListboxProps : undefined}
            componentsProps={
                expandList
                    ? {
                          paper: {
                              sx: {
                                  maxHeight: 'var(--expand-list-max-height, 60vh)',
                                  overflow: 'hidden',
                              },
                          },
                      }
                    : undefined
            }
            onInputChange={onInputChange}
            onBlur={onBlur}
            onFocus={onFocus}
            sx={{
                minWidth,
                ...(selectedInputSx
                    ? { '& .MuiInputBase-root .MuiInputBase-input': selectedInputSx }
                    : {}),
            }}
            renderTags={
                !multiple
                    ? undefined
                    : (tagValue, getTagProps) => {
                          return tagValue.map((option: OptionType, index: number) => {
                              if (index === limitTagsMax) {
                                  return (
                                      <span className="MuiAutocomplete-tag MuiAutocomplete-tagSizeMedium" key={index}>
                                          {'+' + (tagValue.length - limitTagsMax)}
                                      </span>
                                  );
                              }
                              if (index > limitTagsMax) {
                                  return '';
                              }
                              return <Chip label={option.label} {...getTagProps({ index })} disabled={disabled} />;
                          });
                      }
            }
            renderInput={(params) => {
                // if multiple cant be required and empty string for validation
                if (multiple && Array.isArray(selectValues) && selectValues?.length) {
                    // @ts-ignore
                    if (!params.inputProps.value && params.inputProps?.ref?.current !== document.activeElement) {
                        // need to have space for reqiure
                        params.inputProps.value = ' ';
                    }
                }

                if (props.loading) {
                    params.InputProps.startAdornment = (
                        <CircularProgress color="secondary" size={20} style={{ marginLeft: '10px' }} />
                    );
                }

                return (
                    <TextField
                        {...params}
                        variant={variant || 'outlined'}
                        label={label || (required ? ' ' : '')}
                        name={name}
                        size={size || 'medium'}
                        error={error}
                        helperText={helperText}
                        placeholder={placeholder}
                        inputProps={{
                            ...params.inputProps,
                            ...(placeholder ? { placeholder } : {}),
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth={fullWidth}
                        required={required}
                        className={`${(props?.value || []).length ? 'has-value' : ''}`}
                    />
                );
            }}
            renderOption={
                multiple
                    ? (props, option) => (
                          <Box component="li" {...props} key={props.id} sx={optionStyleFce?.(option)}>
                              <Checkbox
                                  value={!!props['aria-selected']}
                                  clickPropagation
                                  sx={{ padding: '0px 10px 0px 0px' }}
                              />
                              {option.label}
                          </Box>
                      )
                    : optionStyleFce
                      ? (props, option) => (
                            <Box
                                component="li"
                                {...props}
                                key={props.id}
                                sx={optionStyleFce(option) ?? undefined}
                            >
                                {option.label}
                            </Box>
                        )
                      : undefined
            }
        />
    );
};

export default Autocomplete;
